import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";

import {
  Badge,
  Button,
  ButtonGroup,
  Text,
  LegacyCard as Card,
  FormLayout,
  Icon,
  Link,
  Modal,
  InlineStack,
  TextField,
  Thumbnail,
  Tooltip,
} from "@shopify/polaris";

import { updateOrderLineTracking, updateOrderLineStatus } from "app/orders/apollo/updateOrderNote";

import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import { statusHelper, baseHelper, imageHelper } from "lib/helpers";
import { NoteIcon } from "@shopify/polaris-icons";
import { useNavigate, useParams } from "react-router-dom";

const UnfulfilledLineItems = (props) => {
  const { data, itemData, onRefetchOrderLineView, onRefetchOrderView, setBanner, setMessage } = props;
  const [trackingCompany] = useState("");
  const [trackingNumber] = useState("");
  const [initialTrackingCompany, setInitialTrackingCompany] = useState("");
  const [initialTrackingNumber, setInitialTrackingNumber] = useState("");

  const [trackingModal, setTrackingModal] = useState(false);

  const [selectedItemData, setSelectedItemData] = useState({});
  const [isAcceptOrder, setIsAcceptedOrder] = useState("");

  const [updateOrderLine, { data: dataUpdateOrderLineTracking, loading: loadingOrderLineTracking }] = useMutation(
    updateOrderLineTracking
  );
  const [updateOrderStatus, { data: updateOrderLineData, loading: orderLineLoading }] = useMutation(
    updateOrderLineStatus
  );
  const { setPageData, pageData, cms } = useContext(PrivateContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { title, breadcrumbs, subtitle } = pageData;
  const processRow = (value, isAccept) => {
    const { _id: id, orderId } = value;
    const formData = { lineItemId: id, orderId, isAccept };

    setIsAcceptedOrder(isAccept);
    updateOrderStatus({ variables: { input: formData } });
  };
  useEffect(() => {
    if (updateOrderLineData) {
      const updateOrderStatusData = baseHelper.getResponseData(
        updateOrderLineData,
        constant.gql.UPDATE_ORDER_LINE_STATUS
      );
      const updateOrderStatusError = baseHelper.getResponseError(
        updateOrderLineData,
        constant.gql.UPDATE_ORDER_LINE_STATUS
      );
      if (updateOrderStatusData && isAcceptOrder) {
        setPageData({ title, breadcrumbs, subtitle });
        setMessage(cms("message.success.orderLineApproved"));
        onRefetchOrderView();
        onRefetchOrderLineView();
      } else if (updateOrderStatusData && !isAcceptOrder) {
        setMessage(cms("message.error.orderLineRejected"));
        onRefetchOrderView();
        onRefetchOrderLineView();
      }
      if (updateOrderStatusError) {
        setBanner({
          status: constant.CRITICAL,
          title: updateOrderStatusError,
          isOpen: true,
        });
      }
    }
  }, [updateOrderLineData]);
  const openTrackingModel = (value) => {
    setSelectedItemData(value);
    setTrackingModal(true);
  };

  const addTrackingDetails = () => {
    if (!initialTrackingCompany) {
      setTrackingModal(false);
      setBanner({ isOpen: true, title: cms("message.error.trackingCompany"), status: "critical" });
      return false;
    }

    if (!initialTrackingNumber) {
      setTrackingModal(false);
      setBanner({ isOpen: true, title: cms("message.error.trackingNumber"), status: "critical" });
      return false;
    }
    // eslint-disable-next-line no-shadow
    const data = {
      trackingNumber: initialTrackingNumber.trim(),
      trackingCompany: initialTrackingCompany.trim(),
    };
    const { _id: id, orderId } = selectedItemData;
    data.id = orderId;
    data.lineItemId = id;
    updateOrderLine({ variables: { input: data } });
    return null;
  };
  useEffect(() => {
    if (dataUpdateOrderLineTracking) {
      const updateOrderTracking = baseHelper.getResponseData(
        dataUpdateOrderLineTracking,
        constant.gql.UPDATE_ORDER_LINE_TRACKING
      );
      const updateOrderTrackingError = baseHelper.getResponseError(
        dataUpdateOrderLineTracking,
        constant.gql.UPDATE_ORDER_LINE_TRACKING
      );

      if (updateOrderTracking) {
        setPageData({ title, breadcrumbs, subtitle });
        setTrackingModal(false);
        setInitialTrackingCompany("");
        setInitialTrackingNumber("");
        setMessage(cms("message.success.orderFulfilled"));
        onRefetchOrderView();
        onRefetchOrderLineView();
      }
      if (updateOrderTrackingError) {
        setTrackingModal(false);
        setBanner({
          status: constant.CRITICAL,
          title: updateOrderTrackingError,
          isOpen: true,
        });
      }
    }
  }, [dataUpdateOrderLineTracking]);

  const acceptOnlyValidInput = (value) => {
    return baseHelper.stringNotAcceptSpaceAtStart(value) && value;
  };

  const renderTracking = () => {
    return (
      <>
        <Modal
          open={trackingModal}
          onClose={() => {
            setTrackingModal(false);
            setInitialTrackingNumber(trackingNumber);
            setInitialTrackingCompany(trackingCompany);
          }}
          title={cms("label.addTracking")}
          primaryAction={{
            content: cms("label.saveDetails"),
            onAction: () => addTrackingDetails(),
            loading: loadingOrderLineTracking,
          }}
          sectioned
        >
          <Modal.Section>
            <FormLayout>
              <TextField
                label={cms("label.company")}
                value={initialTrackingCompany}
                onChange={(val) => {
                  setInitialTrackingCompany(acceptOnlyValidInput(val));
                }}
              />
              <TextField
                label={cms("label.trackingNumber")}
                value={initialTrackingNumber}
                onChange={(val) => {
                  setInitialTrackingNumber(acceptOnlyValidInput(val));
                }}
              />
            </FormLayout>
          </Modal.Section>
        </Modal>
      </>
    );
  };
  const renderTotalAmount = () => {
    const amount = [];
    const vendorsSum = [];
    let countItems = 0;
    const moneyFormat = data.moneyFormat || constant.symbol.DOLLAR;

    itemData.forEach(({ vendor, price, quantity = 1, fulfillmentStatus }) => {
      if (fulfillmentStatus === constant.UNFULFILLED) {
        vendorsSum.push(vendor);
        amount.push(parseFloat(price) * parseInt(quantity, 10));
        countItems += 1;
      }
      return [vendorsSum, amount];
    });

    const vendorSet = new Set(vendorsSum);
    const setSize = vendorSet.size;
    const addVendorTxt =
      setSize === constant.value.ONE ? `${setSize} ${cms("label.vendor")}` : `${setSize} ${cms("label.vendors")}`;
    // const lineDataItems = itemData.length;

    const stringToNumber = amount.map((item) => parseFloat(item));
    const totalAmount = stringToNumber.reduce((cur, acc) => cur + acc, constant.value.ZERO);

    return (
      <>
        <Card.Section>
          <InlineStack align="center">
            <Text as="span" fontWeight="semibold">
              {cms("label.total")}
            </Text>
            <Text variant="bodySm" as="p">
              (
              {countItems > 1
                ? `${countItems}
                ${cms("label.items")} from ${addVendorTxt}`
                : `${countItems}
                ${cms("label.item")} from ${addVendorTxt}`}
              )
            </Text>
            <Text as="span" fontWeight="semibold">{`${moneyFormat} ${totalAmount.toFixed(2)}`}</Text>
          </InlineStack>
        </Card.Section>
      </>
    );
  };
  const renderUnfullfilled = () => {
    const UnfulFilled = itemData.map((value, key) => {
      if (!value || value.fulfillmentStatus !== constant.UNFULFILLED) {
        return null;
      }
      const lineImage =
        imageHelper.resize({ url: value.image, type: constant.imageTypes.THUMBNAIL }) || constant.NOIMAGESNAP;

      const lineItemKey = `lineItem${key}`;
      const { isProductDeleted, isVendorDeleted } = value;

      let isAccepted = false;
      let isFulfilled = false;
      switch (value.status) {
        case constant.ACCEPTED:
          isAccepted = true;
          break;
        case constant.REJECTED:
          isAccepted = false;
          break;
        case constant.STATUS_NEW:
          isAccepted = true;
          break;
        case constant.CLOSED:
          isAccepted = null;
          break;
        default:
          break;
      }
      if (value.fulfillmentStatus === constant.UNFULFILLED) {
        switch (value.status) {
          case constant.ACCEPTED:
            isFulfilled = false;
            break;
          case constant.REJECTED:
            isFulfilled = false;
            break;
          case constant.STATUS_NEW:
            isFulfilled = true;
            break;
          case constant.CLOSED:
            isFulfilled = null;
            break;
          default:
            break;
        }
      }
      const moneyFormat = value.moneyFormat || constant.symbol.DOLLAR;
      const lineTotalPrice = value.price * value.quantity;
      const status = statusHelper.getBadgeStatus(value.status);
      const badgeType = baseHelper.getBadgeType(value.status);
      const lineItemName = <div className="productName">{value.name}</div>;

      return (
        <div className="badge-gap" key={lineItemKey}>
          <Card.Section subdued>
            <InlineStack wrap={false}>
              <Thumbnail size="large" source={lineImage} alt={value.name} />
              <div className="ellipsis">
                {(isProductDeleted && (lineItemName || "")) || (
                  <Link onClick={() => navigate(`/products/edit/${value.productId}`)}>{value.name || ""}</Link>
                )}

                <Text variant="bodySm" as="p">
                  <Text as="span" tone="subdued">
                    {value.vendor && value.vendorId && `${cms("label.vendor")}: `}
                    {isVendorDeleted && value.vendor}
                    {((value.vendor && value.vendorId) || !isVendorDeleted) && (
                      <Link onClick={() => navigate(constant.PROVIDER_VIEW + value.vendorId)}>{value.vendor}</Link>
                    )}
                  </Text>
                  <br />
                  <Text as="span" tone="subdued">
                    {cms("label.skuList")}
                    {value.sku}
                  </Text>
                </Text>

                <InlineStack align="center">
                  <Badge
                    status={
                      (isAccepted && value.status === cms("label.accepted").toLowerCase() && constant.SUCCESS) ||
                      (isAccepted === false && constant.WARNING) ||
                      (isAccepted && value.status === cms("label.new").toLowerCase() && constant.ATTENTION) ||
                      badgeType
                    }
                    className="text-capitalize"
                  >
                    {(isAccepted && value.status === cms("label.accepted").toLowerCase() && cms("label.accepted")) ||
                      (isAccepted === false && cms("label.rejected")) ||
                      (isAccepted && value.status === cms("label.new").toLowerCase() && cms("label.new")) ||
                      status}
                  </Badge>
                </InlineStack>

                {isAccepted && isFulfilled && (
                  <InlineStack align="center">
                    <ButtonGroup>
                      <Tooltip content={constant.REJECT_ORDER}>
                        <div style={{ color: "#bf0711" }}>
                          <Button
                            plain
                            monochrome
                            onClick={() => processRow(value, false, key)}
                            loading={orderLineLoading}
                          >
                            {baseHelper.ucFirst(cms("label.rejectOrder"))}
                          </Button>
                        </div>
                      </Tooltip>
                      <Tooltip content={constant.ACCEPT_ORDER}>
                        <Button
                          size="slim"
                          monochrome
                          outline
                          primary
                          onClick={() => processRow(value, true, key)}
                          loading={orderLineLoading}
                        >
                          {baseHelper.ucFirst(cms("label.acceptOrder"))}
                        </Button>
                      </Tooltip>
                    </ButtonGroup>
                  </InlineStack>
                )}

                {isAccepted && !isFulfilled && (
                  <FormLayout>
                    <InlineStack align="center">
                      <ButtonGroup>
                        <Button size="slim" onClick={() => openTrackingModel(value)}>
                          {baseHelper.ucFirst(cms("label.fulfillItem"))}
                        </Button>
                        <Button variant="plain" size="slim" onClick={() => window.open(`/order-slip/${id}`)}>
                          <InlineStack gap="2">
                            <Icon source={NoteIcon} />
                            <Text as="span">{`${cms("label.packingSlip")}`}</Text>
                          </InlineStack>
                        </Button>
                      </ButtonGroup>
                    </InlineStack>
                  </FormLayout>
                )}
              </div>
              <Text as="span">
                {`${cms("label.qty")}:`}
                {value.quantity}
              </Text>
              <Text as="span">
                {moneyFormat}
                {(lineTotalPrice && lineTotalPrice.toFixed(2)) || constant.value.ZERO}
              </Text>
            </InlineStack>
          </Card.Section>
        </div>
      );
    });
    return UnfulFilled;
  };

  return [renderUnfullfilled(), renderTotalAmount(), renderTracking()];
};

export default UnfulfilledLineItems;
